import {
  BasicTable,
  Warning,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Columns,
  cellStyles1,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';
import {
  commasForNumbers,
  numberToCurrencyFormatter,
} from '../core-utils/helper-utils/helper';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SpendExpectedChart, {
  SpendExpectedChartProps,
} from '../_components/molecules/SpendExpectedChart';
import GifWithLabels from '@HomaHealth/ui-component-library/lib/_patient-access-components/molecules/GIFWithLabels';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import noDataImg from '../../public/Images/NoData.png';
import StopLossAggregateTopCards from './StopLossAggregateTopCards';
import { coverageFieldMap } from '../core-utils/helper-utils/stopLossTriggerHelpers';

export interface reportProps {
  incurredStartDate?: string;
  incurredEndDate?: string;
  paidStartDate?: string;
  paidEndDate?: string;
  isReport?: boolean;
  setIsDataPresent?: React.Dispatch<React.SetStateAction<boolean>>;
  isTable?: boolean;
  max_claim_date?: string;
  aggregationParameter?: string;
  isCurrentMonth?: boolean;
  data?: any;
}

const stopLossAggregateTable: React.FC<reportProps> = ({
  incurredStartDate,
  incurredEndDate,
  data,
}) => {
  const [aggregateReportData, setAggregateReportData] = useState<any[][]>([
    ['', ''],
  ]);
  const [
    spendExpectedData,
    setSpendExpectedData,
  ] = useState<SpendExpectedChartProps>({
    goal: '',
    expected: '',
    maxFunding: '',
    spent: '',
  });
  const [spentExpected, setSpentExpected] = useState<any>('');
  const [currentReserve, setCurrentReserve] = useState<any>('');

  const useStyles = makeStyles()({
    infoBar: {
      borderR: '4px',
      padding: '8px 16px',
      display: 'flex',
      gap: '10px',
      background: '#EDEFF4',
      alignItems: 'center',
    },
  });
  const { classes } = useStyles();

  const getStopLossAggregateTableColumns = (countOfColumns: number) => {
    const columns: Columns[] = [
      {
        name: 'Months',
        align: 'left',
        cellStyles: cellStyles1,
        width: '90px',
      },
      {
        name: 'Enrolled',
        align: 'center',
        cellStyles: cellStyles1,
        width: '90px',
      },
      {
        name: 'Attachment Point',
        align: 'center',
        cellStyles: cellStyles1,
        width: '200px',
        colSpan: 2,
      },
      {
        name: 'Paid Claims',
        align: 'center',
        cellStyles: cellStyles1,
        width: '180px',
        colSpan: 2,
      },
      {
        name: 'YTD Claims',
        align: 'center',
        cellStyles: cellStyles1,
        width: '180px',
        colSpan: countOfColumns > 10 ? 4 : 3,
      },
      // {
      //   name: 'Premium',
      //   align: 'center',
      //   cellStyles: cellStyles1,
      //   width: '100px',
      // },
      {
        name: 'Loss Ratio',
        align: 'center',
        cellStyles: cellStyles1,
        width: '100px',
      },
    ];

    return columns;
  };

  const formatAggregateReportData = (
    data: any,
    level_of_coverage: string[],
  ) => {
    const aggregateReportTableData: any[][] = [];
    let aggregateAttachmentSum = 0;
    let paidClaimsSubjToAggregateSum = 0;
    let paidClaimsOutsideStopLossSum = 0;

    let YTDAttachmentPoint = 0;
    let YTDPaidClaims = 0;

    aggregateReportTableData.push([
      '',
      '',
      'Aggregate',
      'YTD',
      'Medical',
      'RX',
      'Paid Claims',
      'Subject to Agg.',
      'Exceeding Specific Stop Loss',
      'Outside Stop Loss',
      '',
    ]);
    const numToCurrency = (value: number) => {
      return numberToCurrencyFormatter(value, true, 0);
    };

    data.slice(-12)?.forEach((item: any) => {
      const tempRowData = [];
      let index = 0;

      const totalValue = level_of_coverage.reduce((sum, coverageKey) => {
        const dataKey = coverageFieldMap[coverageKey];
        const value = item?.[dataKey] || 0;
        return sum + value;
      }, 0);

      tempRowData[index++] =
        item?.month?.toString()?.substr(0, 3) + ' ' + item?.service_year;
      tempRowData[index++] = totalValue;

      tempRowData[index++] = numToCurrency(item?.Monthly_Aggregate_Attachement);

      tempRowData[index++] = numToCurrency(item?.YTD_Attachement_Point);

      tempRowData[index++] = numToCurrency(item?.Monthly_medical_paid_claims);

      tempRowData[index++] = numToCurrency(item?.Monthly_rx_paid_claims);

      tempRowData[index++] = numToCurrency(item?.YTD_paid_claims);
      tempRowData[index++] = numToCurrency(
        item?.YTD_paid_claims_subj_to_aggregate,
      );

      tempRowData[index++] = numToCurrency(
        item?.YTD_paid_claims_exceeding_specific_stop_loss ?? 0,
      );
      tempRowData[index++] = numToCurrency(
        item?.YTD_paid_claims_outside_stop_loss ?? 0,
      );
      // tempRowData[index++] =
      //   '$' + item?.Monthly_paid_claims_subj_to_aggregate?.toFixed(2);
      // tempRowData[index++] =
      //   item?.Premium === 0 ? '-' : '$' + commasForNumbers(item?.Premium, 0);
      tempRowData[index++] =
        typeof item?.Loss_ratio === 'number'
          ? item?.Loss_ratio?.toFixed(0) + '%'
          : item?.Loss_ratio;
      aggregateReportTableData.push(tempRowData);
      aggregateAttachmentSum += item?.Monthly_Aggregate_Attachement;
      paidClaimsSubjToAggregateSum +=
        item?.Monthly_paid_claims_subj_to_aggregate;
      paidClaimsOutsideStopLossSum +=
        item?.YTD_paid_claims_outside_stop_loss ?? 0;
      YTDAttachmentPoint = item?.YTD_Attachement_Point;
      YTDPaidClaims = item?.YTD_paid_claims_subj_to_aggregate;
    });
    aggregateReportTableData?.push([
      'Total',
      '',
      '$' + commasForNumbers(aggregateAttachmentSum.toString(), 0),
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      ((paidClaimsSubjToAggregateSum / aggregateAttachmentSum) * 100)?.toFixed(
        0,
      ) + '%',
    ]);

    setSpentExpected(
      (
        (paidClaimsSubjToAggregateSum / (aggregateAttachmentSum / 1.25)) *
        100
      ).toFixed(0),
    );
    setCurrentReserve(
      numberToCurrencyFormatter(YTDAttachmentPoint - YTDPaidClaims, true, 0),
    );

    setSpendExpectedData({
      maxFunding: aggregateAttachmentSum.toFixed(0),
      spent: paidClaimsSubjToAggregateSum.toFixed(0),
      expected: (aggregateAttachmentSum / 1.25).toFixed(0),
      goal: ((aggregateAttachmentSum / 1.25) * 0.85).toFixed(0),
    });

    return paidClaimsOutsideStopLossSum > 0
      ? aggregateReportTableData
      : aggregateReportTableData.map((row) =>
          row.filter((_, index) => index !== 9),
        );
  };

  useEffect(() => {
    const allCoverageKeys = [
      'Subscriber',
      'Subscriber and Spouse',
      'Parent and Children',
      'Family',
    ];

    const activeCoverageKeys = data.stopLossSummary?.level_of_coverage?.length
      ? data.stopLossSummary?.level_of_coverage
      : allCoverageKeys;

    setAggregateReportData(
      formatAggregateReportData(data.aggregateReport, activeCoverageKeys),
    );
  }, []);

  const NoDataVisual = () => {
    return (
      <Grid style={{ height: '500px', display: 'flex' }}>
        <GifWithLabels
          mainLabel={
            <Grid className={classes.infoBar}>
              <Typography
                fontSize="14px"
                lineHeight="22.4px"
                textColor={patientAccessTheme.palette.text.lowemphasis}
                fontType="medium"
              >
                {'This group does not have aggregate stop loss coverage'}
              </Typography>
              <Warning />
            </Grid>
          }
          subLabel={''}
          gifUrl={noDataImg}
        />
      </Grid>
    );
  };
  return data?.isAggregateDataPresent ? (
    <>
      <StopLossAggregateTopCards
        stopLossSummaryData={data.stopLossSummary}
        spentExpected={spentExpected}
        currentReserve={currentReserve}
        incurredStartDate={incurredStartDate}
        incurredEndDate={incurredEndDate}
      />
      <Grid style={{ display: 'flex', gap: '1rem', width: '100%' }}>
        <BasicTable
          width={'100%'}
          displayType="box"
          columns={getStopLossAggregateTableColumns(
            aggregateReportData[0].length,
          )}
          rowsData={aggregateReportData}
          cellHeight="32px"
          tableBodyStyles={{
            '& .MuiTableCell-body': {
              color: patientAccessTheme.palette.text.mediumemphasis,
              fontSize: '12px',
              fontWeight: 'inherit !important' as any,
            },
            '& .MuiTableRow-root': {
              '&:last-child': {
                fontWeight: 700,
                fontSize: '14px',
              },
            },
          }}
        />
        <SpendExpectedChart
          spent={spendExpectedData.spent}
          maxFunding={spendExpectedData.maxFunding}
          goal={spendExpectedData.goal}
          expected={spendExpectedData.expected}
          chartWidth={'200'}
          chartHeight={'370'}
          title="Spend vs Expected and Max Funding"
          rootStyles={{ width: '35%', height: '500px' }}
        />
      </Grid>
    </>
  ) : (
    <NoDataVisual />
  );
};

export default stopLossAggregateTable;
